// tinker: chordTour — meet the seven chords. Two modes via config.mode:
//   colors → tap a chord chip 1–7 to hear its triad (softKeys), see its
//            persona word from CHORD_PERSONA, and watch the voicing light up
//            on the piano. The piano stays live for free exploration.
//   jobs   → same chips, but commit-then-reveal per chord (the listen-moment
//            pattern): hear it, pick "home / away / tension" BEFORE any
//            reveal, then get the chord's job from jobOf with a warm line —
//            expected-or-not both respected.
// config.degrees may limit which chips appear (e.g. [1, 4, 5]).
// config.mode: 'minor' (Unit 6) renumbers the tour around E: voicings from
//   MINOR_TRIADS, persona words from MINOR_CHORD_PERSONA, jobs from
//   minorJobOf. config.raisedFive adds a special 5♯ chip (degree token '5+')
//   that plays RAISED_V — "the borrowed doorbell" — in both tour modes.
// Always ready to continue; a chip tap is the resume-audio gesture.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  chordMidis,
  chordPhrase,
  CHORD_PERSONA,
  jobOf,
  MINOR_TRIADS,
  MINOR_CHORD_PERSONA,
  minorJobOf,
  RAISED_V,
} from "../../../lib/homeKeyTheory";
import { PATCHES, playPhrase, resumeAudio } from "../../../lib/homeKeyAudio";
import Piano from "../Piano";
import useHomeKeyVoices from "../useHomeKeyVoices";
import useTypingKeys from "../useTypingKeys";
import useHomeKeyMidi from "../useHomeKeyMidi";

const ALL_DEGREES = [1, 2, 3, 4, 5, 6, 7];

// The raised-five chip stores the theory lib's string token.
const RAISED = "5+";

const IMPRESSIONS = [
  { id: "home", text: "feels like home" },
  { id: "away", text: "away — out wandering" },
  { id: "tension", text: "tension — it needs to move" },
];

const JOB_WORDS = { home: "home", away: "away", tension: "tension" };

const chipLabel = (degree) => (degree === RAISED ? "5♯" : degree);

const revealLine = (label, persona, job, impression) => {
  if (impression === job) {
    return `Your ear called it — the ${label} chord (${persona}) is a ${JOB_WORDS[job]} chord.`;
  }
  return `Most ears file the ${label} chord (${persona}) under ${JOB_WORDS[job]} — but you heard ${JOB_WORDS[impression]}, and that's a real way to hear it. Jobs are tendencies, not laws.`;
};

export default function TinkerChordTour({ moment, onReadyChange }) {
  const config = moment.config || {};
  const isMinor = config.mode === "minor";
  const mode = isMinor ? config.tourMode || "colors" : config.mode || "colors";
  const baseDegrees =
    config.degrees && config.degrees.length ? config.degrees : ALL_DEGREES;
  let degrees = baseDegrees;
  if (config.raisedFive) {
    degrees = baseDegrees.reduce((list, d) => {
      list.push(d);
      if (d === 5) list.push(RAISED); // the borrowed doorbell sits by 5
      return list;
    }, []);
    // degrees configs that skip 5 still get the chip, at the end
    if (!degrees.includes(RAISED)) degrees.push(RAISED);
  }

  const personaOf = (degree) => {
    if (degree === RAISED) {
      return MINOR_CHORD_PERSONA[RAISED] || "the borrowed doorbell";
    }
    return isMinor ? MINOR_CHORD_PERSONA[degree] : CHORD_PERSONA[degree];
  };
  const voicingOf = (degree) => {
    if (degree === RAISED) return RAISED_V;
    return isMinor ? MINOR_TRIADS[degree] || [] : chordMidis(degree);
  };
  const jobFor = (degree) => {
    if (degree === RAISED) return "tension"; // borrowed exactly to pull home
    return isMinor ? minorJobOf(degree) : jobOf(degree);
  };
  const phraseFor = (degree) =>
    isMinor || degree === RAISED
      ? chordPhrase([degree], { mode: "minor" })
      : chordPhrase([degree]);

  const [picked, setPicked] = useState(null); // degree last tapped
  const [ringing, setRinging] = useState(null); // Set of midis lit while it rings
  const [impression, setImpression] = useState(null); // jobs mode: committed chip
  const playRef = useRef(null);
  const ringTimer = useRef(null);

  const voices = useHomeKeyVoices(PATCHES.softKeys);
  const { press, release, activeMidis } = voices;
  const pianoLive = mode === "colors";
  useTypingKeys(press, release, pianoLive);
  useHomeKeyMidi(press, release, pianoLive);

  useEffect(() => {
    if (onReadyChange) onReadyChange(true);
  }, [onReadyChange]);

  const stopAll = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    if (ringTimer.current) clearTimeout(ringTimer.current);
    ringTimer.current = null;
    setRinging(null);
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  const tapChord = (degree) => {
    resumeAudio();
    stopAll();
    const handle = playPhrase(phraseFor(degree), PATCHES.softKeys);
    playRef.current = handle && handle.stop ? handle : null;
    setPicked(degree);
    if (mode === "jobs") setImpression(null); // a fresh chord, a fresh guess
    setRinging(new Set(voicingOf(degree)));
    ringTimer.current = setTimeout(
      () => setRinging(null),
      ((handle && handle.durMs) || 1200) + 300
    );
  };

  const lit = ringing ? new Set([...activeMidis, ...ringing]) : activeMidis;

  const defaultPrompt =
    mode === "jobs"
      ? "Tap a chord, listen, and file it by feel — home, away, or tension — before we tell you its job. Your ear goes first."
      : "Every chord is a number with a color. Tap one, let it ring, and watch which keys it lives on. No wrong order — just wander.";

  return (
    <div className="hk-moment-body">
      <p>{config.prompt || defaultPrompt}</p>

      <div className="hk-degree-chips" role="group" aria-label="chords by degree">
        {degrees.map((degree) => (
          <button
            key={degree}
            type="button"
            className={`hk-degree-chip${
              degree === RAISED ? " hk-degree-chip--raised" : ""
            }${picked === degree ? " hk-degree-chip--on" : ""}`}
            aria-pressed={picked === degree}
            onClick={() => tapChord(degree)}
          >
            {chipLabel(degree)}
          </button>
        ))}
      </div>

      {mode === "colors" && picked != null && (
        <p className="hk-persona" aria-live="polite">
          <strong>the {chipLabel(picked)} chord</strong> — {personaOf(picked)}
        </p>
      )}

      {mode === "jobs" && picked != null && (
        <>
          <div
            className="hk-choice-row"
            role="group"
            aria-label="how does this chord feel?"
          >
            {IMPRESSIONS.map((choice) => (
              <button
                key={choice.id}
                type="button"
                className={`hk-choice${impression === choice.id ? " hk-choice--picked" : ""}`}
                aria-pressed={impression === choice.id}
                onClick={() => setImpression(choice.id)}
              >
                {choice.text}
              </button>
            ))}
          </div>
          {impression != null && (
            <p className="hk-listen-response" aria-live="polite">
              {revealLine(
                chipLabel(picked),
                personaOf(picked),
                jobFor(picked),
                impression
              )}
            </p>
          )}
        </>
      )}

      {mode === "colors" && (
        <Piano
          activeMidis={lit}
          onPress={press}
          onRelease={release}
          highlight={{ scaleOnly: true }}
        />
      )}
    </div>
  );
}
